import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Library, Sparkles, CreditCard, Settings, LogOut, Home,
  Dumbbell, ClipboardCheck, Camera, NotebookPen, UserCog, Menu, X,
} from "lucide-react";
import { useAuth, isCoach, roleLabel } from "@/context/AuthContext";

const COACH_NAV = [
  ["/coach", "Command", LayoutDashboard], ["/coach/clients", "Clients", Users],
  ["/coach/templates", "Templates", Dumbbell], ["/coach/library", "Library", Library],
  ["/coach/ai", "AI Studio", Sparkles], ["/coach/billing", "Billing", CreditCard],
  ["/coach/settings", "Settings", Settings],
];
const CLIENT_NAV = [
  ["/app", "Today", Home], ["/app/plans", "My Plans", Dumbbell], ["/app/logs", "Workout Logs", NotebookPen],
  ["/app/checkin", "Check-in", ClipboardCheck], ["/app/progress", "Progress Photos", Camera],
  ["/app/billing", "Membership", CreditCard], ["/app/account", "Account", UserCog],
];

function NavItems({ items, onClick }) {
  return items.map(([to, label, Icon]) => (
    <NavLink key={to} to={to} end={to === "/coach" || to === "/app"} onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => `nav-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${isActive
        ? "bg-[#FF3B30]/10 text-white border-l-2 border-[#FF3B30]" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
      <Icon size={17} strokeWidth={1.8} /> {label}
    </NavLink>
  ));
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = isCoach(user) ? COACH_NAV : CLIENT_NAV;
  const doLogout = async () => { await logout(); nav("/login"); };

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 flex items-center gap-2">
        <div className="w-8 h-8 bg-[#FF3B30] grid place-items-center rounded-sm font-display font-extrabold text-lg">K</div>
        <div>
          <div className="font-display text-xl font-bold tracking-wide leading-none">K-FITHUB</div>
          <div className="eyebrow mt-1">{isCoach(user) ? "Coach OS" : "Athlete"}</div>
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1"><NavItems items={items} onClick={() => setOpen(false)} /></nav>
      <div className="p-4 border-t border-[#1E293B]">
        <div className="text-sm font-medium truncate" data-testid="shell-user-name">{user?.name}</div>
        <div className="text-xs text-slate-500 truncate">{user?.email} · {roleLabel(user?.role)}</div>
        <button onClick={doLogout} data-testid="logout-btn"
          className="mt-3 flex items-center gap-2 text-xs text-slate-400 hover:text-[#FF3B30] transition-colors">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex relative z-10">
      <aside className="hidden lg:block w-60 shrink-0 border-r border-[#1E293B] bg-[#0B0F17] sticky top-0 h-screen">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#0B0F17] border-r border-[#1E293B]">{sidebar}</div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-[#1E293B] sticky top-0 bg-[#0B0F17]/90 backdrop-blur-md z-40">
          <span className="font-display text-lg font-bold">K-FITHUB</span>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">{open ? <X /> : <Menu />}</button>
        </div>
        <main className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] mx-auto">{children}</main>
      </div>
    </div>
  );
}
